import { Trans } from '@lingui/macro'
import { AddCircle } from '@mui/icons-material'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Icon,
  IconButton,
  Typography
} from '@mui/material'
import { BlobProvider } from '@react-pdf/renderer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DIRECTORS_ADMIN_GROUP } from 'app/auth/authRoles'
import { queryCreateObject, queryGetObject, queryGetObjects } from 'app/backend/Common'
import { Application } from 'app/backend/salesforce-maps/Application'
import { Case } from 'app/backend/salesforce-maps/Case'
import { CaseComment } from 'app/backend/salesforce-maps/CaseComment'
import { ContentVersion, SF_CONTENT_VERSION_TYPES } from 'app/backend/salesforce-maps/ContentVersion'
import { FormPage } from 'app/backend/salesforce-maps/FormPage'
import { createFundingStreamFieldErrorLog } from 'app/backend/selesforce-custom-queries/ErrorLogQueries'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import { ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import {
  addComment,
  committeeCaseMapping,
  createCaseByFlow,
  createVote,
  entityTypes,
  getVote,
  updateVote
} from 'app/services/sfAuth/sfData/sfCase'
import { getDocumentsByEntity } from 'app/services/sfAuth/sfData/sfFiles'
import { getFormPage } from 'app/services/sfAuth/sfData/sfForms'
import {
  closedStages,
  getOpportunityStage
} from 'app/services/sfAuth/sfData/sfOpportunity'
import { getExternalReviews } from 'app/services/sfAuth/sfData/sfReviews'
import { generateUploadFileName } from 'app/utils/generateUploadFileName'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { RadioGroupWithFormik } from 'app/views/common-components/with-formik/RadioGroupWithFormik'
import { constructFormAddressString } from 'app/views/forms/common/Common'
import Form from 'app/views/forms/Form'
import { NOT_FOUND_PATH } from 'app/views/sessions/SessionRoutes'
import Loading from 'egret/components/EgretLoadable/Loading'
import { Formik } from 'formik'
import { isNil, isNumber } from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { isValidSalesforceId, valueExistsTS } from 'utilsTS'
import { GRANTS_PATH } from '../GrantsConstants'
import AllocationComitteQuestionsCard from './AllocationComitteQuestionsCard'
import { COMMITTEES_ALLOWING_CONFLICT_OF_INTEREST } from './CommitteesUtils'
import { checkIsMemberForCommittee } from './committeeUtils'

export const fundLogos = {
  'Community-Based Tenant Initiative Fund (CBTIF)':
    '/assets/images/funds-icons-cbti.png',
  'Sector Transformation Fund – Sectoral Impact':
    '/assets/images/funds-icons-stf-sip.png',
  'Sector Transformation Fund – Local Project':
    '/assets/images/funds-icons-stf-lp.png',
  // TODO replace FCHI image with correct one
  'FCHI-2': '/assets/images/funds-icons-stf-lp.png',
  'ICCE Grant 2021': '/assets/images/icce-logo-circle.svg',
  'ICCE - Community Funding Program - Round 3':
    '/assets/images/icce-logo-circle.svg',
  'ICCE - Community Funding Program': '/assets/images/icce-logo-circle.svg',
  'Green Kickstarter Fund': '/assets/images/funds-icons-greenfund.png'
}

const ReviewApplications = (): JSX.Element => {
  const [loadingCompleted, setLoadingCompleted] = useState(false)
  const [application, setApplication] = useState<Application>()
  const [maxVotes, setMaxVotes] = useState<number>(0)
  const [blockPDF, setBlockPDF] = useState(false)
  const [currentVotes, setCurrentVotes] = useState(0)
  const [voteObjectId, setVoteObjectId] = useState(null)
  const [userVote, setUserVote] = useState<any>(null)
  const [canVote, setCanVote] = useState(false)
  const [form, setForm] = useState<FormPage>()
  const [firstReview, setFirstReview] = useState(false)
  const [inDepthReview, setInDepthReview] = useState(false)
  const [submittingVote, setSubmittingVote] = useState<boolean>(false)
  const [canAddComments, setCanAddComments] = useState<boolean>(false)

  // Stores the PDF blob generated for the final vote submission
  const [pdfBlob, setPdfBlob] = useState(null)

  const user = useSelector<ReduxState, ReduxUser>((state) => state.user)
  const isDirectorsAdmin = user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)

  const { fundingStreams } = useGlobalQueries()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const history = useHistory()
  const { id: applicationId, committee } = useParams()
  const { network } = useGlobalQueries()
  const queryClient = useQueryClient()
  const committeeType = String(committee).toUpperCase()

  const existingFilesQuery = useQuery({
    queryFn: async () =>
      await getDocumentsByEntity(applicationId, {
        Type__c: SF_CONTENT_VERSION_TYPES.EXECUTIVE_SUMMARY
      }),
    queryKey: ['executiveSummaryExistingFiles'],
    initialData: []
  })

  // Ref for questions that is used to save questions with comments before refetching
  const questionsRef = useRef<Case[]>([])

  // React Query for auto-fetching questions/comments
  const questionsQuery = useQuery({
    queryKey: ['cases', applicationId, committeeType],
    queryFn: async () => {
      if (isNil(applicationId) || isNil(committeeType)) return []
      const cases = await queryGetObjects({
        type: 'Case',
        searchProps: {
          Associated_Opportunity__c: applicationId,
          Type: committeeCaseMapping[committeeType]
        },
        include: [{ base: 'CaseComments', select: ['*', 'CreatedBy.Id', 'CreatedBy.Name'] }]
      })
      const sortedCases = cases.map(caseItem => {
        if (!isNil(caseItem.comments)) {
          caseItem.comments.sort((a, b) => {
            const dateA = isNil(a.createdDate) ? 0 : new Date(a.createdDate).getTime()
            const dateB = isNil(b.createdDate) ? 0 : new Date(b.createdDate).getTime()
            return dateA - dateB
          })
        }
        return caseItem
      }).sort((a, b) => {
        const dateA = isNil(a.createdDate) ? 0 : new Date(a.createdDate).getTime()
        const dateB = isNil(b.createdDate) ? 0 : new Date(b.createdDate).getTime()
        return dateA - dateB
      })
      return combineQuestions(sortedCases, questionsRef.current)
    },
    enabled: !isNil(applicationId) && !isNil(committeeType),
    refetchOnWindowFocus: true,
    staleTime: 1 * 60 * 1000
  })

  // Sync questionsRef when query data changes
  useEffect(() => {
    if (!isNil(questionsQuery.data)) {
      questionsRef.current = questionsQuery.data
    }
  }, [questionsQuery.data])

  // Mutation for creating questions
  const createQuestionMutation = useMutation({
    mutationFn: async ({ description }: { description: string }) => {
      const result = await createCaseByFlow({
        type: committeeCaseMapping[committeeType],
        language: user.language,
        description,
        opportunityId: applicationId,
        userName: user.displayName,
        contact: user.userObject.contactId,
        owner: user.userId
      })
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cases', applicationId, committeeType] })
      enqueueSnackbar(<Trans>Question saved</Trans>, { variant: 'info' })
    },
    onError: () => {
      enqueueSnackbar(<Trans>Error while saving question</Trans>, { variant: 'error' })
    }
  })

  // Mutation for creating comments
  const createCommentMutation = useMutation({
    mutationFn: async ({ questionId, comment }: { questionId: string, comment: string }) => {
      await addComment(questionId, { comment })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cases', applicationId, committeeType] })
      enqueueSnackbar(<Trans>Comment saved</Trans>, { variant: 'info' })
    },
    onError: () => {
      enqueueSnackbar(<Trans>Error while saving comment</Trans>, { variant: 'error' })
    }
  })

  const fetchData = async (): Promise<void> => {
    if (!isValidSalesforceId(applicationId)) {
      history.push(NOT_FOUND_PATH)
      return
    }

    console.log('got committee type', committeeType)
    const toQuery = [
      'Id',
      'LastModifiedDate',
      'RecordTypeId',
      'Assigned_Program_Manager__c',
      'StageName',
      'FGM_Base__Recommended_Amount__c',
      'AccountId',
      'RecordType.*',
      'FGM_Base__Amount_Requested__c',
      'Funding_Stream__c',
      'Funding_Stream__r.*',
      'FGM_Portal__Total_Project_Budget__c',
      'Other_grant_requests_related_to_project__c',
      'Submit_Date__c',
      'Summary__c',
      'Hardcoded_executive_summary__c',
      'Account.Name',
      'Account.Id',
      'Description',
      'Priority_areas__c',
      'Geographical_scope__c',
      'Geographical_Location__c',
      'Demographic__c',
      'Target_demographics__c',
      'Other_target_demographic__c',
      'Units_women_and_children__c',
      'Units_single_parent__c',
      'Units_racialized_persons__c',
      'Units_Indigenous__c',
      'Units_fleeing_domestic_violence__c',
      'Supported_demographics__c',
      'Other_supported_demographic__c',
      'Impact_summary__c',
      'Partners_summary__c',
      'PM_Name__c',
      'PM_Recommendation__c',
      'PM_Recommendation_TXT__c',
      'Name'
    ]

    if (committeeType.includes('CMHC')) {
      toQuery.push('CMHC_signer__c')
    }

    const application = await queryGetObject({
      type: 'Application',
      id: applicationId,
      select: toQuery,
      include: [
        {
          base: 'FGM_Base__Reviews__r',
          select: [
            'CMHC_Priority_areas_touched__c',
            'RecordType.Name',
            'FGM_Base__Review_Date__c'
          ]
        },
        {
          base: 'OpportunityTeamMembers',
          select: ['UserId', 'TeamMemberRole', 'OpportunityAccessLevel']
        }
      ]
    })

    const votes = await getVote(applicationId, undefined, entityTypes[committeeType])

    const vote = votes.find((obj) => obj.CreatedById === user.userId)
    const currentVotes = votes.filter(
      (vote) =>
        vote.Conflict_of_interest__c !== 'declared_conflict' &&
        ['For', 'Against'].includes(vote.Result__c)
    ).length

    const config = fundingStreams.find(
      (fs) => fs.id === application.fundingStream
    )
    const configKey = committeeType !== 'BOARD' ? committeeType : 'Board'
    let formId = config?.[configKey]
    if (!isNil(config) && isNil(formId)) {
      createFundingStreamFieldErrorLog(configKey, history.location.pathname, config.id)
    }

    if (application.shouldUseHardcodedExecutiveSummary === true && config?.hardcodedExecutiveSummaryForm != null) {
      formId = config.hardcodedExecutiveSummaryForm
    }

    let voteValue = 'None'
    if (valueExistsTS(vote?.Result__c)) {
      voteValue = vote.Result__c
    }

    const isConflictOfInterest = valueExistsTS(vote) && vote.Conflict_of_interest__c === 'declared_conflict'
    const noConflictReview = !valueExistsTS(vote)

    const isSCMember = checkIsMemberForCommittee(application, user, 'SC')
    const isACMember = checkIsMemberForCommittee(application, user, 'AC')

    const canVote = Boolean(
      (isACMember && committeeType === 'AC') ||
        (isSCMember && committeeType === 'SC')
    )

    const canAddComments = canVote || isDirectorsAdmin || application?.assignedProgramManager === user.userId
    const maxVotes = application.fundingStreamFull?.totalRequiredVotes
    setBlockPDF(committeeType === 'CMHC' && application.cmhcSigner == null)

    if (noConflictReview && !isDirectorsAdmin) {
      enqueueSnackbar(
        <Trans>NO_CONFLICT_OF_INTEREST_REVIEW_SNACKBAR</Trans>,
        {
          variant: 'info'
        }
      )
      history.push(`${GRANTS_PATH.EXECUTIVE_SUMMARIES}/${committeeType}`)
    } else if (isConflictOfInterest) {
      enqueueSnackbar(
        <Trans>NO_ACCESS_DUE_TO_CONFLICT_OF_INTEREST_SNACKBAR</Trans>,
        {
          variant: 'info'
        }
      )
      history.push(`${GRANTS_PATH.EXECUTIVE_SUMMARIES}/${committeeType}`)
    }

    console.log('loaded vote', vote)
    console.log('loaded application', application)

    setUserVote(voteValue)
    setMaxVotes(maxVotes ?? 0)
    setCurrentVotes(currentVotes)
    setApplication(application)
    setCanAddComments(canAddComments)
    setCanVote(canVote)
    setVoteObjectId(vote?.Id)

    if (valueExistsTS(formId)) {
      Promise.all([
        getFormPage(formId),
        getExternalReviews({
          FGM_Base__Request__c: applicationId
        })
      ]).then(([form, externalReviews]) => {
        let firstReview, inDepthReview
        externalReviews.forEach((review) => {
          const recordType = String(review.RecordType.Name).toLowerCase()
          if (recordType.includes('first')) {
            firstReview = review.Id
          } else if (recordType.includes('depth')) {
            inDepthReview = review.Id
          }
        })

        console.log('found form for opportunity view', form)
        setFirstReview(firstReview)
        setInDepthReview(inDepthReview)
        if (form != null) {
          setForm(form)
        }
        setLoadingCompleted(true)
      }, reject => {

      })
    } else {
      setLoadingCompleted(true)
    }
  }

  useEffect(() => {
    void fetchData()
  }, [])

  const addNewComment = (id, values, setFieldValue): void => {
    const newQuestions = [...values.questions]
    newQuestions[id].comments.push({
      createdBy: {
        name: user.displayName,
        id: user.userId
      },
      comment: '',
      editable: true,
      tempId: `temp-${Date.now()}-${Math.random()}`
    })
    questionsRef.current = newQuestions
    setFieldValue('questions', newQuestions)
  }

  const handleSaveComment = async (questionId, commentId, values, setFieldValue): Promise<void> => {
    const question = values.questions[questionId]
    const comment = question.comments[commentId]

    if (isNil(question.id) || isNil(comment.comment)) return

    // Remove the editable comment from questionsRef to prevent duplication
    // Don't update Formik state - let CommentCard's disabled state handle the UI
    const newQuestions = values.questions.map((q, idx) => {
      if (idx === questionId) {
        return {
          ...q,
          comments: q.comments.filter((_, cIdx) => cIdx !== commentId)
        }
      }
      return q
    })
    questionsRef.current = newQuestions

    await createCommentMutation.mutateAsync({
      questionId: question.id,
      comment: comment.comment
    })
  }

  const handleSaveQuestion = async (index, values, setFieldValue): Promise<void> => {
    const question = values.questions[index]
    if (isNil(question.description)) return

    // Remove the editable question from questionsRef to prevent duplication
    // Don't update Formik state - let the disabled state handle the UI
    const newQuestions = values.questions.filter((_, idx) => idx !== index)
    questionsRef.current = newQuestions

    await createQuestionMutation.mutateAsync({
      description: question.description
    })
  }

  const reloadStage = async (vote): Promise<void> => {
    return await Promise.all([
      getOpportunityStage(applicationId),
      getVote(applicationId, undefined, entityTypes[committeeType])
    ]).then(([stage, votes]) => {
      const currentVotes = votes.filter(
        (vote) =>
          vote.Conflict_of_interest__c !== 'declared_conflict' &&
          ['For', 'Against'].includes(vote.Result__c)
      ).length
      setApplication((prevApplication) => {
        if (prevApplication == null) return prevApplication
        return {
          ...prevApplication,
          stageName: stage
        }
      })
      setCurrentVotes(currentVotes)
      setSubmittingVote(false)
    })
  }

  const submitVote = async (voteValue): Promise<void> => {
    console.log('submitting vote', voteValue)
    setSubmittingVote(true)

    const infoSnackbar = enqueueSnackbar(<Trans>Submitting vote</Trans>, {
      variant: 'info',
      persist: true
    })

    // onEnd function reloads the stage and closes the snackbar.
    // It is called at the very end of the createVote or updateVote functions
    const onEnd = async (voteValue): Promise<void> => {
      (voteValue === 'conflict_of_interest' ? Promise.resolve(true) : reloadStage(voteValue))
        .then((result) => {
          closeSnackbar(infoSnackbar)
          enqueueSnackbar(<Trans>Vote submitted</Trans>, {
            variant: 'success'
          })
          if (voteValue === 'conflict_of_interest') {
            history.push(`${GRANTS_PATH.EXECUTIVE_SUMMARIES}/${committeeType}`)
          }
        }, reject => {
          console.warn('onEnd Vote', reject)
        })
    }

    try {
      const result = voteObjectId != null
        ? await updateVote({ vote: voteValue, updateId: voteObjectId })
        : await createVote({
          voteValue,
          id: applicationId,
          userId: user.userId,
          entityType: entityTypes[committeeType]
        })

      console.log('vote saved', voteValue, result.id)
      setUserVote(voteValue)
      if (voteObjectId == null) setVoteObjectId(result.id)

      // cut out due to problems with permissions to the field
      // tasks.push(
      //   updateOpportunity({
      //     Id: applicationId,
      //     Executive_summary_form__c: form?.id
      //   })
      // )

      if (voteValue !== 'conflict_of_interest') {
        if (pdfBlob != null) {
          try {
            await uploadPdfInOpportunity(pdfBlob)
          } catch (uploadErr) {
            console.error('Error uploading PDF after voting!', uploadErr)
            enqueueSnackbar(<Trans>ERROR_PDF_UPLOAD_AFTER_VOTE</Trans>, {
              variant: 'error'
            })
          }
        } else {
          console.error('No PDF generated for upload after voting!')
        }
      }

      await onEnd(voteValue)
    } catch (err) {
      console.error('error saving vote', err)
      setSubmittingVote(false)
      closeSnackbar(infoSnackbar)
      enqueueSnackbar(<Trans>Error while submitting vote</Trans>, {
        variant: 'error'
      })
    }
  }

  /**
   * Uploads pdf file to the opportunity
   * @param {Blob} blob - pdf file to upload
   * @param {Function} onUpload - callback function to call after file is uploaded
   */
  const uploadPdfInOpportunity = async (blob): Promise<void> => {
    const freshFilesData = await existingFilesQuery.refetch()
    const files = freshFilesData.data ?? []
    const reader = new FileReader()

    reader.onloadend = async () => {
      const existingFile = files.find(
        (file) =>
          file.ContentVersion?.Type__c ===
          SF_CONTENT_VERSION_TYPES.EXECUTIVE_SUMMARY
      )

      const name = generateUploadFileName({
        type: SF_CONTENT_VERSION_TYPES.EXECUTIVE_SUMMARY,
        opportunity: {
          Name: application?.name ?? ''
        },
        existingDocsCount: files.length
      })

      if (!(reader.result instanceof ArrayBuffer)) return

      const base64data = Buffer.from(reader.result).toString('base64')
      const contentVersionData: Partial<ContentVersion> = {
        title: name,
        pathOnClient: name,
        networkId: network?.id,
        type: SF_CONTENT_VERSION_TYPES.EXECUTIVE_SUMMARY,
        versionData: base64data
      }

      if (existingFile != null) {
        contentVersionData.contentDocument = existingFile.ContentDocumentId
      } else {
        contentVersionData.firstPublishLocationId = applicationId
      }

      return await queryCreateObject({
        type: 'Content Version',
        createData: contentVersionData
      }).catch(uploadErr => {
        const stringifiedLog = JSON.stringify({
          type: 'PDF creation fail',
          reject: String(uploadErr),
          objectType: 'Opportunity',
          objectId: applicationId,
          fileName: name
        })
        void queryCreateObject({
          type: 'Error Log',
          createData: {
            severity: 'error',
            description: sfOauthConfig.oauth2.redirectUri + history.location.pathname,
            longDescription: stringifiedLog.length < 131072 ? stringifiedLog : 'Error log was too long'
          }
        }).catch(() => {})
        throw uploadErr
      })
    }

    reader.readAsArrayBuffer(blob)
  }

  if (!loadingCompleted || network == null || form == null) {
    return <Loading atCenter />
  }

  let showReviewed = false
  let canBeReviewed = false

  if (application != null) {
    showReviewed =
      (isNumber(application.recommendedAmount) && application.recommendedAmount <= 50000 &&
        !committeeType.includes('SC')) ||
      closedStages.includes(application.stageName)
    canBeReviewed = committeeType === application.stageName
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        questions: isNil(questionsQuery.data) ? [] : questionsQuery.data,
        vote: userVote
      }}
      persist
      onSubmit={() => {}}
    >
      {({ setFieldValue, setValues, values }) => (
        <ReviewQuestions
          setFieldValue={setFieldValue}
          setValues={setValues}
          values={values}
          blockPDF={blockPDF}
          form={form}
          committeeType={committeeType}
          canVote={canVote}
          canBeReviewed={canBeReviewed}
          showReviewed={showReviewed}
          maxVotes={maxVotes}
          currentVotes={currentVotes}
          userVote={userVote}
          submittingVote={submittingVote}
          submitVote={submitVote}
          pdfBlob={pdfBlob}
          setPdfBlob={setPdfBlob}
          canAddComments={canAddComments}
          questionsRef={questionsRef}
          addNewComment={addNewComment}
          handleSaveQuestion={handleSaveQuestion}
          handleSaveComment={handleSaveComment}
          application={application}
          user={user}
          applicationId={applicationId}
          firstReview={firstReview}
          inDepthReview={inDepthReview}
        />
      )}
    </Formik>
  )
}

interface ReviewQuestionsProps {
  setFieldValue: any
  setValues: any
  values: any
  blockPDF: boolean
  form: any
  committeeType: string
  canVote: boolean
  canBeReviewed: boolean
  showReviewed: boolean
  maxVotes: number
  currentVotes: number
  userVote: any
  submittingVote: boolean
  submitVote: (vote: any) => Promise<any>
  pdfBlob: any
  setPdfBlob: any
  canAddComments: boolean
  questionsRef: any
  addNewComment: any
  handleSaveQuestion: any
  handleSaveComment: any
  application: any
  user: any
  applicationId: any
  firstReview: any
  inDepthReview: any
}

const ReviewQuestions = ({
  setFieldValue,
  setValues,
  values,
  blockPDF,
  form,
  committeeType,
  canVote,
  canBeReviewed,
  showReviewed,
  maxVotes,
  currentVotes,
  userVote,
  submittingVote,
  submitVote,
  pdfBlob,
  setPdfBlob,
  canAddComments,
  questionsRef,
  addNewComment,
  handleSaveQuestion,
  handleSaveComment,
  application,
  user,
  applicationId,
  firstReview,
  inDepthReview
}: ReviewQuestionsProps): JSX.Element => {
  const { questions } = values

  useEffect(() => {
    questionsRef.current = questions
  }, [questions, questionsRef])

  return (
    <Grid
      container
      spacing={1}
      justifyContent='space-between'
      style={{ height: '100%' }}
    >
      <Grid item xs={6}>
        <Form
          blockPDFDownload={blockPDF}
          defaultFormType='printable'
          mainObject='Opportunity'
          formId={form.id}
          banner={
            blockPDF &&
            committeeType.includes('CMHC') && {
              title: <Trans>CMHC_REVIEW_NO_PDF_DOWLOAD_BANNER</Trans>,
              type: 'info'
            }
          }
          fetchString={constructFormAddressString({
            ids: {
              User: user.userId,
              Account: application?.account.id,
              Opportunity: applicationId,
              FGM_Base__Review__c: [firstReview, inDepthReview],
              Funding_Stream__c: application?.fundingStream
            },
            objectsConnected: form.objectsConnected
          })}
          fixedDisplay
        />
      </Grid>

      <Grid item xs={6}>
        {showReviewed
          ? (
            <div style={{ paddingTop: 30 }}>
              <Grid
                style={{
                  width: '100%',
                  paddingTop: 30,
                  paddingBottom: 30
                }}
                container
                direction='row'
                alignItems='center'
                justifyContent='center'
              >
                <Typography
                  sx={{
                    fontSize: '48px',
                    textAlign: 'center',
                    padding: '20px'
                  }}
                >
                  <Trans>Reviewed</Trans>
                </Typography>
                <Icon
                  color='secondary'
                  style={{ fontSize: 48, margin: 10 }}
                >
                  check_box
                </Icon>
              </Grid>
            </div>
            )
          : (
            <div
              style={{ paddingTop: 30, paddingBottom: 30 }}
            >
              {canVote && (
                <div>
                  <Typography style={{ fontSize: 16, marginBottom: 24 }}>
                    <b>
                      <Trans>Vote</Trans>
                    </b>
                  </Typography>
                  <Typography style={{ fontSize: 14, marginBottom: 12 }}>
                    <b>
                      <Trans>REVIEW_APPLICATIONS_CURRENT_VOTES_LABEL</Trans>
                      :
                    </b>{' '}
                    {maxVotes != null
                      ? `${currentVotes} / ${maxVotes}`
                      : currentVotes}
                  </Typography>
                  <Typography style={{ fontSize: 14 }}>
                    <Trans>REVIEW_APPLICATIONS_MY_VOTE_LABEL</Trans>
                    {': '}
                    <b>
                      <Trans id={userVote} />
                    </b>
                  </Typography>
                  {canBeReviewed && (
                    <Grid
                      gap='16px'
                      justifyContent='center'
                      alignItems='center'
                      container
                    >
                      <RadioGroupWithFormik
                        name='vote'
                        margin='normal'
                        disabled={submittingVote || !canVote}
                        options={[
                          { label: <Trans>For</Trans>, value: 'For' },
                          {
                            label: <Trans>Against</Trans>,
                            value: 'Against'
                          },
                          ...(committeeType !== 'CMHC'
                            ? [
                                {
                                  label: (
                                    <Trans>Request Live Discussion</Trans>
                                  ),
                                  value: 'Request Live Discussion'
                                }
                              ]
                            : [])
                        ]}
                        row
                      />
                      <Button
                        variant='contained'
                        disabled={submittingVote}
                        onClick={() => {
                          void submitVote(values.vote)
                        }}
                      >
                        <Trans>Vote</Trans>!
                      </Button>

                      {COMMITTEES_ALLOWING_CONFLICT_OF_INTEREST.includes(committeeType) && (
                        <ConflictOfInterestButton
                          disabled={submittingVote}
                          handleSubmit={(e) => {
                            void submitVote('conflict_of_interest')
                          }}
                        />
                      )}

                      <Form
                        blockPDFDownload={blockPDF}
                        defaultFormType='pdf'
                        mainObject='Opportunity'
                        formId={form.id}
                        fetchString={constructFormAddressString({
                          ids: {
                            User: user.userId,
                            Account: application?.account.id,
                            Opportunity: applicationId,
                            FGM_Base__Review__c: [firstReview, inDepthReview],
                            Funding_Stream__c: application?.fundingStream
                          },
                          objectsConnected: form.objectsConnected
                        })}
                        hideLoading
                        returnPdf={(pdfDocument, formTitlePdf, fetchData) => {
                          return (
                            <BlobProvider document={pdfDocument}>
                              {({ blob, url, loading, error }) => {
                                if (pdfBlob == null) {
                                  setPdfBlob(blob)
                                }
                                return <div />
                              }}
                            </BlobProvider>
                          )
                        }}
                      />
                    </Grid>
                  )}
                </div>
              )}

              <div style={{ width: '80%' }}>
                <Grid
                  container
                  alignItems='center'
                  style={{
                    marginBottom: 12
                  }}
                >
                  <Typography align='center' style={{ fontSize: 18 }}>
                    {committeeType !== 'CMHC'
                      ? (
                        <Trans>Questions & Answers</Trans>
                        )
                      : (
                        <Trans>Comments</Trans>
                        )}
                  </Typography>
                  {(canVote || canAddComments) && (
                    <IconButton
                      disabled={!canVote && !canAddComments}
                      sx={{
                        color: 'primary.main'
                      }}
                      onClick={() => {
                        const newValues = { ...values }
                        newValues.questions.push({
                          question: '',
                          suppliedName: user.displayName,
                          editable: true,
                          comments: []
                        })
                        setValues(newValues)
                        questionsRef.current = newValues.questions
                      }}
                    >
                      <AddCircle style={{ fontSize: 36 }} />
                    </IconButton>
                  )}
                </Grid>
              </div>

              {questions.map((question, index) => {
                return (
                  <AllocationComitteQuestionsCard
                    key={index}
                    canAddComments={canAddComments || canVote}
                    disabled={!canBeReviewed}
                    index={index}
                    handleSaveQuestion={async (index) =>
                      handleSaveQuestion(index, values, setFieldValue)}
                    handleSaveComment={async (questionId, commentId) =>
                      handleSaveComment(
                        questionId,
                        commentId,
                        values,
                        setFieldValue
                      )}
                    handleComment={() => {
                      addNewComment(index, values, setFieldValue)
                    }}
                    {...question}
                  />
                )
              })}
            </div>
            )}
      </Grid>
    </Grid>
  )
}

const ConflictOfInterestButton = ({ disabled, handleSubmit }): JSX.Element => {
  const [warning, showWarning] = useState(false)

  return (
    <>
      <Button
        disabled={disabled}
        variant='contained'
        onClick={(e) => {
          showWarning(true)
        }}
      >
        <Trans>Conflict of interest</Trans>
      </Button>
      <Dialog open={warning} fullWidth maxWidth='xs'>
        <DialogTitleWithIconClose
          handleClose={(e) => {
            showWarning(false)
          }}
          label={
            <Trans>Are you sure you want to declare conflic of interest?</Trans>
          }
        />
        <DialogContent>
          <Alert severity='warning'>
            <Trans>You cannot undo this decision</Trans>
          </Alert>

          <Grid container direction='column' gap='8px' sx={{ mt: '8px' }}>
            <Button
              variant='contained'
              fullWidth
              onClick={(e) => {
                showWarning(false)
                handleSubmit()
              }}
            >
              <Trans>Yes</Trans>
            </Button>
            <Button
              variant='outlined'
              fullWidth
              onClick={(e) => showWarning(false)}
            >
              <Trans>No</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

const combineQuestions: (refetchedQuestions: Case[], currentQuestions: Case[]) => Case[] = (refetchedQuestions, currentQuestions) => {
  const questions = [...refetchedQuestions]
  for (let i = 0; i < currentQuestions.length; i++) {
    const currentQuestion = currentQuestions[i]
    const questionIndex = questions.findIndex((q: Case) => (q.id === currentQuestion.id) && !isNil(currentQuestion.id))
    if (questionIndex === -1) {
      questions.push(currentQuestion)
    } else {
      questions[questionIndex].comments = combineComments(
        questions[questionIndex].comments ?? [],
        isNil(currentQuestion.comments) ? [] : currentQuestion.comments
      )
    }
  }
  return questions
}

const combineComments: (refetchedComments: CaseComment[], currentComments: CaseComment[]) => CaseComment[] = (refetchedComments, currentComments) => {
  const comments = [...refetchedComments]
  for (let i = 0; i < currentComments.length; i++) {
    const currentComment = currentComments[i]
    const commentIndex = comments.findIndex((c: CaseComment) => (c.id === currentComment.id) && !isNil(currentComment.id))
    if (commentIndex === -1) {
      comments.push(currentComment)
    }
  }
  return comments
}

export default ReviewApplications
